import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage } from '../utils/errorHandling';
import {
  notesApi,
  Note,
  CreateNoteDto,
  UpdateNoteDto,
  GetNotesDto,
  NoteStats,
  NoteCategory,
  NotePriority,
} from '../api/notes';

interface UseNotesReturn {
  // Data
  notes: Note[];
  statistics: NoteStats | null;
  loading: boolean;
  error: string | null;
  
  // Operations
  createNote: (data: CreateNoteDto) => Promise<Note | null>;
  updateNote: (id: string, data: UpdateNoteDto) => Promise<Note | null>;
  deleteNote: (id: string) => Promise<boolean>;
  togglePin: (noteId: string) => Promise<Note | null>;
  toggleArchive: (noteId: string) => Promise<Note | null>;
  refreshData: () => Promise<void>;
  
  // Utility functions
  getNoteById: (id: string) => Note | undefined;
  getNotesByBird: (birdId: string) => Note[];
  getNotesBySpecies: (speciesId: string) => Note[];
  getNotesByCategory: (category: NoteCategory) => Note[];
  getNotesByPriority: (priority: NotePriority) => Note[];
  getPinnedNotes: () => Note[];
  getArchivedNotes: () => Note[];
  getNotesWithReminders: () => Note[];
}

export const useNotes = (initialFilters: GetNotesDto = {}): UseNotesReturn => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [statistics, setStatistics] = useState<NoteStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use ref to track current filters without causing dependency loops
  const currentFiltersRef = useRef(initialFilters);
  currentFiltersRef.current = initialFilters;

  // Load notes data - stable function reference
  const loadNotes = useCallback(async (filters: GetNotesDto = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const notes = await notesApi.getNotes(filters);
      setNotes(notes);
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to load notes');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load statistics - stable function reference
  const loadStatistics = useCallback(async () => {
    try {
      const stats = await notesApi.getStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to load note statistics:', err);
    }
  }, []);

  // Create stable function references for operations
  const loadStatisticsRef = useRef(loadStatistics);
  loadStatisticsRef.current = loadStatistics;

  // Create note
  const createNote = useCallback(async (data: CreateNoteDto) => {
    try {
      const newNote = await notesApi.createNote(data);
      setNotes(prev => [newNote, ...prev]);
      toast.success('Note created successfully');
      await loadStatistics();
      return newNote;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to create note');
      toast.error(errorMessage);
      throw err;
    }
  }, [loadStatistics]);

  // Update note
  const updateNote = useCallback(async (id: string, data: UpdateNoteDto) => {
    try {
      const updatedNote = await notesApi.updateNote(id, data);
      setNotes(prev => 
        prev.map(note => 
          note.id === id ? updatedNote : note
        )
      );
      toast.success('Note updated successfully');
      await loadStatistics();
      return updatedNote;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to update note');
      toast.error(errorMessage);
      throw err;
    }
  }, [loadStatistics]);

  // Delete note
  const deleteNote = useCallback(async (id: string) => {
    try {
      await notesApi.deleteNote(id);
      setNotes(prev => prev.filter(note => note.id !== id));
      toast.success('Note deleted successfully');
      await loadStatistics();
      return true;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to delete note');
      toast.error(errorMessage);
      return false;
    }
  }, [loadStatistics]);

  // Toggle pin (server flips the current state)
  const togglePin = useCallback(async (noteId: string): Promise<Note | null> => {
    try {
      const updatedNote = await notesApi.togglePin(noteId);

      setNotes(prev =>
        prev.map(n => n.id === noteId ? updatedNote : n)
      );

      toast.success(updatedNote.isPinned ? 'Note pinned' : 'Note unpinned');
      await loadStatisticsRef.current();
      return updatedNote;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to toggle pin');
      toast.error(errorMessage);
      return null;
    }
  }, []);

  // Toggle archive (server flips the current state)
  const toggleArchive = useCallback(async (noteId: string): Promise<Note | null> => {
    try {
      const updatedNote = await notesApi.toggleArchive(noteId);

      setNotes(prev =>
        prev.map(n => n.id === noteId ? updatedNote : n)
      );

      toast.success(updatedNote.isArchived ? 'Note archived' : 'Note unarchived');
      await loadStatisticsRef.current();
      return updatedNote;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to toggle archive');
      toast.error(errorMessage);
      return null;
    }
  }, []);

  // Create stable function references
  const loadNotesRef = useRef(loadNotes);
  loadNotesRef.current = loadNotes;

  // Refresh data - stable function reference
  const refreshData = useCallback(async () => {
    await Promise.all([
      loadNotesRef.current(currentFiltersRef.current),
      loadStatisticsRef.current(),
    ]);
  }, []);

  // Utility functions
  const getNoteById = useCallback((id: string): Note | undefined => {
    return notes.find(note => note.id === id);
  }, [notes]);

  const getNotesByBird = useCallback((birdId: string): Note[] => {
    return notes.filter(note => note.birdId === birdId);
  }, [notes]);

  const getNotesBySpecies = useCallback((speciesId: string): Note[] => {
    return notes.filter(note => note.speciesId === speciesId);
  }, [notes]);

  const getNotesByCategory = useCallback((category: NoteCategory): Note[] => {
    return notes.filter(note => note.category === category);
  }, [notes]);

  const getNotesByPriority = useCallback((priority: NotePriority): Note[] => {
    return notes.filter(note => note.priority === priority);
  }, [notes]);

  const getPinnedNotes = useCallback((): Note[] => {
    return notes.filter(note => note.isPinned);
  }, [notes]);

  const getArchivedNotes = useCallback((): Note[] => {
    return notes.filter(note => note.isArchived);
  }, [notes]);

  const getNotesWithReminders = useCallback((): Note[] => {
    return notes.filter(note => note.reminderDate);
  }, [notes]);

  // Load data on mount - only runs once
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        loadNotes(),
        loadStatistics(),
      ]);
    };
    
    loadInitialData();
  }, []); // Empty dependency array - only run on mount

  return {
    // Data
    notes,
    statistics,
    loading,
    error,
    
    // Operations
    createNote,
    updateNote,
    deleteNote,
    togglePin,
    toggleArchive,
    refreshData,
    
    // Utility functions
    getNoteById,
    getNotesByBird,
    getNotesBySpecies,
    getNotesByCategory,
    getNotesByPriority,
    getPinnedNotes,
    getArchivedNotes,
    getNotesWithReminders,
  };
}; 